import { useState } from 'react';
import { Landmark, Scale, Shield, TriangleAlert } from 'lucide-react';
import {
  Button,
  DataTable,
  EffectiveValue,
  ErrorState,
  FilterBar,
  FormField,
  MetricCard,
  MetricRow,
  Modal,
  OpenFlag,
  ReasonField,
  Select,
  SelectableChip,
  StatusPill,
  TextField,
  isReasonValid,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate, today } from '../../lib/format/dates';
import { formatCents, formatRate } from '../../lib/format/money';
import { defaultedSort, sortRows, type SortValue } from '../../lib/table/clientSort';
import { useTableUrlState, type TableSort } from '../../lib/table/tableSearch';
import { notify } from '../../services/notify';
import { pickParam } from '../profiles/people';
import type { HomeRuleCity } from './api';
import { useRegisterCity } from './leversQueries';
import { useHomeRule } from './queries';

const VIEWS = ['all', 'unregistered', 'registered'] as const;

/**
 * Sort values per column. Every home rule city is loaded at once (no paging), so
 * sorting is client-side. A missing value (no rate, no exposure) sorts last in
 * either direction; a registered city's coverage is "collected in full", above
 * any ratio.
 */
const SORT_VALUE: Record<string, SortValue<HomeRuleCity>> = {
  city: (r) => r.city.toLowerCase(),
  registered: (r) => (r.registered ? 1 : 0),
  gross: (r) => r.grossCents,
  rate: (r) => (r.registered ? undefined : r.cityRatePct),
  exposure: (r) => r.estimatedExposureCents,
  accrued: (r) => r.bufferAccruedCents,
  coverage: (r) => (r.registered ? Number.POSITIVE_INFINITY : r.coverageRatio),
  buffer: (r) => (r.bufferSource ? r.effectiveBufferPct : undefined),
};

/** Biggest sellers first when the URL names no sort. */
export const DEFAULT_SORT: TableSort = { key: 'gross', dir: 'desc' };

export const sortHomeRule = (rows: HomeRuleCity[], sort: TableSort | null) =>
  sortRows(rows, sort, SORT_VALUE, (a, b) => a.city.localeCompare(b.city));
const VIEW_LABEL = { all: 'All', unregistered: 'Unregistered', registered: 'Registered' };

function columns(lock: string | undefined, onRegister: (c: HomeRuleCity) => void): DataTableColumn<HomeRuleCity>[] {
  return [
    {
      id: 'city',
      sortKey: 'city',
      header: 'City',
      width: 'minmax(130px, 1fr)',
      cell: (r) => <span className="font-semibold">{r.city}</span>,
    },
    {
      id: 'registered',
      sortKey: 'registered',
      header: 'Registered',
      width: '130px',
      cell: (r) => (
        <span>
          <StatusPill tone={r.registered ? 'success' : 'danger'} dot>
            {r.registered ? 'Yes' : 'No'}
          </StatusPill>
          {r.registeredSince && (
            <span className="mt-0.5 block text-[11px] text-muted">since {formatDate(r.registeredSince, 'UTC')}</span>
          )}
        </span>
      ),
    },
    {
      id: 'gross',
      sortKey: 'gross',
      header: 'Gross sales',
      width: '120px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCents(r.grossCents, { whole: true }),
    },
    {
      id: 'rate',
      sortKey: 'rate',
      header: 'City rate',
      width: '90px',
      align: 'right',
      numeric: true,
      cell: (r) =>
        r.registered ? '—' : r.cityRatePct === undefined ? <OpenFlag /> : formatRate(r.cityRatePct, 'fraction'),
    },
    {
      id: 'exposure',
      sortKey: 'exposure',
      header: 'Est. uncollected',
      width: '150px',
      align: 'right',
      numeric: true,
      cell: (r) =>
        r.estimatedExposureCents === undefined ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="font-semibold text-danger">{formatCents(r.estimatedExposureCents)}</span>
        ),
    },
    {
      id: 'accrued',
      sortKey: 'accrued',
      header: 'Buffer accrued',
      width: '140px',
      align: 'right',
      numeric: true,
      cell: (r) => formatCents(r.bufferAccruedCents),
    },
    {
      id: 'coverage',
      sortKey: 'coverage',
      header: 'Coverage',
      width: '150px',
      cell: (r) => {
        if (r.registered)
          return (
            <StatusPill tone="success" dot>
              Collected in full
            </StatusPill>
          );
        if (r.coverageRatio === undefined) return <span className="text-muted">—</span>;
        const pct = Math.round(r.coverageRatio * 100);
        return (
          <span className="inline-flex w-full items-center gap-2">
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-[rgba(143,68,32,.1)]">
              <span
                className={`block h-full rounded-full ${pct >= 100 ? 'bg-green' : pct >= 60 ? 'bg-olive' : 'bg-danger'}`}
                style={{ width: `${Math.min(pct, 100)}%` }}
              />
            </span>
            <span className="tabular-nums">{pct}%</span>
          </span>
        );
      },
    },
    {
      id: 'buffer',
      sortKey: 'buffer',
      header: 'Effective buffer',
      width: '220px',
      cell: (r) =>
        r.bufferSource ? (
          <EffectiveValue value={formatRate(r.effectiveBufferPct, 'fraction')} source={r.bufferSource} />
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      id: 'act',
      header: '',
      width: '110px',
      align: 'right',
      action: true,
      cell: (r) =>
        !r.registered ? (
          <Button
            variant="ghost-dark"
            size="sm"
            lockedReason={lock}
            onClick={(e) => {
              e.stopPropagation();
              onRegister(r);
            }}
          >
            Register
          </Button>
        ) : null,
    },
  ];
}

/**
 * Colorado home rule (kit `HomeRuleScreen`; §7.10). Home-rule cities collect
 * their own tax; unregistered, we do not collect it and the buffer accrues
 * against the exposure. "Register" records the license and sets the city's
 * buffer to 0% in one step (§7.5).
 */
export function HomeRuleScreen() {
  const table = useTableUrlState();
  const { locked } = useSession();
  const view = pickParam(table.filters.view, VIEWS) ?? 'all';
  const q = useHomeRule();
  const [registering, setRegistering] = useState<HomeRuleCity | null>(null);
  const all = q.data?.rows ?? [];
  const { sort, onSortChange } = defaultedSort(table.sort, DEFAULT_SORT, table.setSort);
  const rows = sortHomeRule(
    all.filter((r) => view === 'all' || (view === 'registered') === r.registered),
    sort,
  );
  const t = q.data?.totals;
  const n = (v: string | number | undefined) => (v !== undefined ? v : q.isError ? '—' : '…');
  const ratio =
    t && t.estimatedExposureCents > 0 ? `${Math.round((t.bufferAccruedCents / t.estimatedExposureCents) * 100)}%` : '—';

  return (
    <div className="flex flex-col gap-[18px]">
      <MetricRow>
        <MetricCard
          label="Home rule cities"
          value={n(q.data ? all.length : undefined)}
          hint={q.data ? `${all.filter((r) => r.transactionCount > 0).length} with orders` : undefined}
          accent="var(--admin-chart-2)"
          icon={<Landmark size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Estimated exposure"
          value={n(t && formatCents(t.estimatedExposureCents, { whole: true }))}
          hint="uncollected city tax"
          accent="var(--admin-chart-4)"
          icon={<TriangleAlert size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Buffer accrued"
          value={n(t && formatCents(t.bufferAccruedCents, { whole: true }))}
          hint="reserved against it"
          accent="var(--admin-chart-3)"
          icon={<Shield size={15} aria-hidden="true" />}
        />
        <MetricCard
          label="Coverage ratio"
          value={n(t && ratio)}
          hint="accrued ÷ exposure"
          accent="var(--admin-chart-1)"
          icon={<Scale size={15} aria-hidden="true" />}
        />
      </MetricRow>

      <div>
        <FilterBar>
          {VIEWS.map((v) => (
            <SelectableChip
              key={v}
              active={view === v}
              onClick={() => table.setFilter('view', v === 'all' ? undefined : v)}
            >
              {VIEW_LABEL[v]}
            </SelectableChip>
          ))}
          {q.data?.citiesWithNoRateOnFile?.length ? (
            <span className="ml-auto">
              <OpenFlag>no rate on file: {q.data.citiesWithNoRateOnFile.join(', ')}</OpenFlag>
            </span>
          ) : null}
        </FilterBar>
        <DataTable
          label="Colorado home rule cities"
          minWidth={1240}
          columns={columns(locked('tax.write'), setRegistering)}
          rows={rows}
          getRowId={(r) => r.cityId}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          sort={sort}
          onSortChange={onSortChange}
          emptyLabel="No home rule cities match that filter."
        />
      </div>

      {registering && <RegisterDialog city={registering} onClose={() => setRegistering(null)} />}
    </div>
  );
}

function RegisterDialog({ city, onClose }: { city: HomeRuleCity; onClose: () => void }) {
  const { profile } = useSession();
  const register = useRegisterCity(city.cityId);
  const [license, setLicense] = useState('');
  const [effective, setEffective] = useState(today(profile?.timezone));
  const [frequency, setFrequency] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  const [nextDue, setNextDue] = useState('');
  const [whoFiles, setWhoFiles] = useState<'stripe' | 'kurtz_fargo' | 'internal'>('kurtz_fargo');
  const [reason, setReason] = useState('');
  const ready = license.trim() && effective && isReasonValid(reason);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={`Register ${city.city}`}
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready}
            loading={register.isPending}
            onClick={() =>
              register.mutate(
                {
                  licenseNumber: license.trim(),
                  effectiveDate: effective,
                  filingFrequency: frequency,
                  nextDueDate: nextDue || undefined,
                  whoFiles,
                  reason: reason.trim(),
                },
                {
                  onSuccess: () => {
                    notify.success(`${city.city} registered — its buffer is now 0%.`);
                    onClose();
                  },
                },
              )
            }
          >
            Register city
          </Button>
        </>
      }
    >
      <p className="mt-0 text-sm text-muted">
        Records the license as loaded into Stripe and sets {city.city}'s buffer to{' '}
        <strong className="text-ink">0%</strong> in the same step — once we collect the city's tax, eaters stop paying a
        buffer for it.
      </p>
      <div className="grid gap-3.5">
        <div className="grid grid-cols-2 gap-3.5">
          <FormField label="License number" htmlFor="reg-license" required>
            <TextField id="reg-license" surface="white" value={license} onChange={(e) => setLicense(e.target.value)} />
          </FormField>
          <FormField label="Effective date" htmlFor="reg-effective" required>
            <TextField
              id="reg-effective"
              surface="white"
              type="date"
              value={effective}
              onChange={(e) => setEffective(e.target.value)}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-3 gap-3.5">
          <FormField label="Filing frequency" htmlFor="reg-frequency">
            <Select
              id="reg-frequency"
              surface="white"
              value={frequency}
              options={[
                { value: 'monthly', label: 'Monthly' },
                { value: 'quarterly', label: 'Quarterly' },
                { value: 'annual', label: 'Annual' },
              ]}
              onChange={(e) => setFrequency(e.target.value as typeof frequency)}
            />
          </FormField>
          <FormField label="Next due" htmlFor="reg-due" optional>
            <TextField
              id="reg-due"
              surface="white"
              type="date"
              value={nextDue}
              onChange={(e) => setNextDue(e.target.value)}
            />
          </FormField>
          <FormField label="Who files" htmlFor="reg-who">
            <Select
              id="reg-who"
              surface="white"
              value={whoFiles}
              options={[
                { value: 'kurtz_fargo', label: 'Kurtz Fargo' },
                { value: 'stripe', label: 'Stripe' },
                { value: 'internal', label: 'Internal' },
              ]}
              onChange={(e) => setWhoFiles(e.target.value as typeof whoFiles)}
            />
          </FormField>
        </div>
        <ReasonField value={reason} onChange={setReason} />
      </div>
      {register.error && <ErrorState error={register.error} compact />}
    </Modal>
  );
}
